/**
 * The React half of `lib/window-sync.ts`: which window this is, the main
 * window's side (open, track and feed the layout windows), and a layout
 * window's side (follow the main window).
 *
 * The role and the channel are page facts, decided once at module load — a
 * window does not change what it is.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { mintUlid, type UserWorkspace } from "@qufolio/model";

import type { SaveStatus, WorkspaceFollower } from "../api/persistence";
import { popupFeatures } from "./detach";
import type { WorkspaceAction } from "./workspace-actions";
import {
  CHANNEL,
  isFor,
  layoutWindowName,
  layoutWindowUrl,
  windowRole,
  type WindowMessage,
  type WindowRole,
} from "./window-sync";

export const ROLE: WindowRole = windowRole(globalThis.location?.search ?? "", mintUlid);

const channel: BroadcastChannel | null =
  typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);

function post(message: WindowMessage): void {
  channel?.postMessage(message);
}

/** A layout window's way to the workspace; `undefined` in a main window. */
export const FOLLOWER: WorkspaceFollower | undefined =
  ROLE.kind === "layout" && channel
    ? {
        send: (action) => post({ t: "action", main: ROLE.mainId, action }),
        subscribe: (onState) => {
          const hear = (event: MessageEvent) => {
            const message = event.data as unknown;
            if (isFor(message, ROLE.mainId) && message.t === "state") {
              onState(message.workspace, message.status as SaveStatus);
            }
          };
          channel.addEventListener("message", hear);
          post({ t: "need-state", main: ROLE.mainId });
          return () => channel.removeEventListener("message", hear);
        },
      }
    : undefined;

/**
 * **The chart clicked last, in any window of this main window** — what a data
 * layout's cells follow when their own layout has no chart. Every window both
 * says and hears it, so the cells agree wherever they are: a data layout popped
 * out to a second monitor follows a chart clicked on the first.
 */
let lastFocus: RemoteFocus | null = null;
/**
 * Each layout's own chart clicked last, by layout id — what a data layout that
 * follows one layout shows (`lib/data-layout.ts` `followOf`). Not the same as
 * `lastFocus`: clicking Default must not move a grid that follows Two Charts
 * off the chart last clicked *there*.
 */
let focusByLayout: Readonly<Record<string, string | null>> = {};
const focusListeners = new Set<() => void>();
function heardFocus(focus: RemoteFocus): void {
  if (lastFocus?.layoutId === focus.layoutId && lastFocus.chartId === focus.chartId) return;
  lastFocus = focus;
  focusByLayout = { ...focusByLayout, [focus.layoutId]: focus.chartId };
  for (const listener of focusListeners) listener();
}

function subscribeFocus(listener: () => void): () => void {
  focusListeners.add(listener);
  return () => focusListeners.delete(listener);
}

export function useFocusByLayout(): Readonly<Record<string, string | null>> {
  return useSyncExternalStore(subscribeFocus, () => focusByLayout);
}
channel?.addEventListener("message", (event: MessageEvent) => {
  const message = event.data as unknown;
  if (isFor(message, ROLE.mainId) && message.t === "focus") {
    heardFocus({ layoutId: message.layoutId, chartId: message.chartId });
  }
});

export function useLastFocus(): RemoteFocus | null {
  return useSyncExternalStore(subscribeFocus, () => lastFocus);
}

/** A chart was clicked here — told to this window and to every other. */
export function announceFocus(layoutId: string, chartId: string | null): void {
  heardFocus({ layoutId, chartId });
  post({ t: "focus", main: ROLE.mainId, layoutId, chartId });
}

/** The layout a detached data panel follows when the window clicked last is not the main one. */
export interface RemoteFocus {
  layoutId: string;
  chartId: string | null;
}

/**
 * The main window's side. Inert in a layout window.
 *
 * - **Feeds** every layout window the workspace after each change, at most
 *   every 100 ms: a divider drag changes it 60 times a second and only the last
 *   matters to a window that is not being dragged.
 * - **Applies** their edits through its own `dispatch`, which is what saves.
 * - **Tracks** the windows it opened, so the tab can say ⧉ and a click can
 *   bring the window forward. `closed` is polled, as `DetachedWindow` does: a
 *   window shut from the OS chrome does not always say so.
 * - **Closes** them when it goes: a layout window without its main window has
 *   nobody to save its edits.
 */
export function useLayoutWindows(
  workspace: UserWorkspace,
  status: SaveStatus,
  dispatch: (action: WorkspaceAction) => void,
): {
  elsewhere: readonly string[];
  openLayout: (layoutId: string) => boolean;
  focusLayout: (layoutId: string) => void;
  remoteFocus: RemoteFocus | null;
} {
  const main = ROLE.kind === "main";
  const windows = useRef(new Map<string, Window>());
  const [elsewhere, setElsewhere] = useState<string[]>([]);
  const [remoteFocus, setRemoteFocus] = useState<RemoteFocus | null>(null);

  const latest = useRef({ workspace, status });
  latest.current = { workspace, status };
  const sendState = useCallback(() => {
    post({ t: "state", main: ROLE.mainId, workspace: latest.current.workspace, status: latest.current.status });
  }, []);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!main || windows.current.size === 0) return;
    if (timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      sendState();
    }, 100);
  }, [main, workspace, status, sendState]);

  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;
  useEffect(() => {
    if (!main || !channel) return;
    const hear = (event: MessageEvent) => {
      const message = event.data as unknown;
      if (!isFor(message, ROLE.mainId)) return;
      if (message.t === "action") dispatchRef.current(message.action);
      else if (message.t === "need-state") sendState();
      else if (message.t === "focus") setRemoteFocus({ layoutId: message.layoutId, chartId: message.chartId });
    };
    channel.addEventListener("message", hear);
    return () => channel.removeEventListener("message", hear);
  }, [main, sendState]);

  // A click in this window makes it the one clicked last again.
  useEffect(() => {
    if (!main) return;
    const mine = () => setRemoteFocus(null);
    window.addEventListener("pointerdown", mine, true);
    return () => window.removeEventListener("pointerdown", mine, true);
  }, [main]);

  useEffect(() => {
    if (!main) return;
    const poll = window.setInterval(() => {
      let changed = false;
      for (const [id, win] of windows.current) {
        if (win.closed) {
          windows.current.delete(id);
          changed = true;
        }
      }
      if (changed) setElsewhere([...windows.current.keys()]);
    }, 700);
    const shut = () => {
      for (const win of windows.current.values()) win.close();
    };
    window.addEventListener("pagehide", shut);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener("pagehide", shut);
    };
  }, [main]);

  const openLayout = useCallback(
    (layoutId: string) => {
      if (!main) return false;
      const existing = windows.current.get(layoutId);
      if (existing && !existing.closed) {
        existing.focus();
        return true;
      }
      const opened = window.open(
        layoutWindowUrl(layoutId, ROLE.mainId),
        layoutWindowName(layoutId),
        popupFeatures({ width: window.outerWidth * 0.8, height: window.outerHeight * 0.8 }),
      );
      if (!opened) return false;
      windows.current.set(layoutId, opened);
      setElsewhere([...windows.current.keys()]);
      return true;
    },
    [main],
  );

  const focusLayout = useCallback((layoutId: string) => {
    windows.current.get(layoutId)?.focus();
  }, []);

  return { elsewhere, openLayout, focusLayout, remoteFocus };
}
